import React, { useEffect, useRef, useState, useCallback } from 'react';

import { useChatScrollMemory } from '@hooks/useChatScrollMemory';
import { usePromptTurns } from '@hooks/usePromptTurns';
import CrossIcon from '@icon/CrossIcon2';
import useStore from '@store/store';
import { MessageInterface, PromptTurn } from '@type/chat';
import { resolveAdjacentTurn } from '@utils/chatScrollMemoryQueries';
import { debug } from '@utils/debug';

import ChatTitle from './ChatTitle';
import CloneChat from './CloneChat';
import DownloadChat from './DownloadChat';
import Message from './Message';
import NewMessageButton from './Message/NewMessageButton';
import PromptTurnNavigation from './PromptTurnNavigation';
import ScrollToBottomButton from './ScrollToBottomButton';

interface ErrorMessageProps {
  error: string;
  onClose: () => void;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ error, onClose }) => (
  <div className='relative py-2 px-3 w-3/5 mt-3 max-md:w-11/12 border rounded-md border-red-500 bg-red-500/10'>
    <div className='text-gray-600 dark:text-gray-100 text-sm whitespace-pre-wrap'>
      {error}
    </div>
    <button
      className='text-white absolute top-1 right-1 cursor-pointer'
      onClick={onClose}
      aria-label="Close error message"
    >
      <CrossIcon />
    </button>
  </div>
);

const ChatContent: React.FC = () => {
  // Add temporary variable to disable NewMessageButton
  const in_future = true;

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const saveRef = useRef<HTMLDivElement | null>(null);

  // State
  const [showScrollButton, setShowScrollButton] = useState(false);

  const inputRole = useStore((state) => state.inputRole);
  const error = useStore((state) => state.error);
  const setError = useStore((state) => state.setError);
  const generating = useStore((state) => state.generating);
  const hideSideMenu = useStore((state) => state.hideSideMenu);
  const layoutWidth = useStore((state) => state.layoutWidth);
  const currentChat = useStore((state) =>
    state.chats &&
    state.currentChatIndex >= 0 &&
    state.currentChatIndex < state.chats.length
      ? state.chats[state.currentChatIndex]
      : undefined
  );

  // Get messages
  const messages = currentChat?.messages ?? [];

  // Get sticky index
  const stickyIndex = useStore((state) =>
    state.chats &&
    state.chats.length > 0 &&
    state.currentChatIndex >= 0 &&
    state.currentChatIndex < state.chats.length
      ? (state.chats[state.currentChatIndex]?.messages.length ?? 0)
      : 0
  );

  const turns = usePromptTurns(messages);
  const turnStartMessageIds = new Set(turns.map((turn: PromptTurn) => turn.startMessageId));
  const { activeTurnStartMessageId, scrollToMessageId } = useChatScrollMemory({
    chatId: currentChat?.id,
    messages,
    turns,
    containerRef: chatContainerRef,
    initialActiveTurnStartMessageId: currentChat?.viewState?.activeTurnStartMessageId,
  });

  const activeTurnIndex = turns.findIndex(
    (turn: PromptTurn) => turn.startMessageId === activeTurnStartMessageId
  );

  const goToTurn = useCallback(
    (offset: -1 | 1) => {
      if (turns.length === 0) {
        debug.log('chat', '[ChatContent] goToTurn skipped: no turns available', {
          chatId: currentChat?.id,
          offset,
        });
        return;
      }

      const { currentIndex, nextIndex, turn: nextTurn } = resolveAdjacentTurn({
        turns,
        activeTurnStartMessageId,
        offset,
      });

      debug.log('chat', '[ChatContent] goToTurn requested', {
        chatId: currentChat?.id,
        offset,
        currentIndex,
        nextIndex,
        activeTurnStartMessageId,
        nextTurnStartMessageId: nextTurn?.startMessageId,
      });

      if (nextTurn) {
        scrollToMessageId(nextTurn.startMessageId);
      }
    },
    [activeTurnStartMessageId, currentChat?.id, scrollToMessageId, turns]
  );

  // Scroll handlers
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = chatContainerRef.current;
    if (container instanceof HTMLDivElement) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (container instanceof HTMLDivElement) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    }
  }, []);

  // Layout width helper
  const getWidthClass = () => {
    if (layoutWidth === 'wide') {
      return hideSideMenu ? 'w-[55%]' : 'w-[55%]';
    }
    return hideSideMenu ? 'w-[40%]' : 'w-[40%]';
  };

  // Effects
  useEffect(() => {
    // Effect function
    const handleGeneratingChange = () => {
      if (generating) {
        setError('');
      }
    };
    
    // Call it immediately
    handleGeneratingChange();
    
    // No cleanup needed
    return undefined;
  }, [generating, setError]);

  useEffect(() => {
    debug.log('chat', '[ChatContent] active chat render context', {
      chatId: currentChat?.id,
      messageCount: messages.length,
      turnCount: turns.length,
      savedViewState: currentChat?.viewState,
      activeTurnStartMessageId,
    });
  }, [activeTurnStartMessageId, currentChat?.id, currentChat?.viewState, messages.length, turns.length]);

  // Setup scroll listener
  useEffect(() => {
    const container = chatContainerRef.current;
    
    // Define cleanup function
    const cleanup = () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
    
    // Add event listener if container exists
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    // Always return the cleanup function
    return cleanup;
  }, [handleScroll]);

  return (
    <div className='relative flex-1 overflow-hidden'>
      <div
        ref={chatContainerRef}
        className="h-full overflow-y-auto dark:bg-gray-800 scroll-smooth"
      >
        <div className='flex flex-col items-center text-sm dark:bg-gray-800'>
          <div
            className='flex flex-col items-center text-sm dark:bg-gray-800 w-full'
            ref={saveRef}
          >
            <ChatTitle />

            {/* New Message Button when no messages */}
            {!generating && messages?.length === 0 && !in_future && (
              <NewMessageButton messageIndex={-1} />
            )}

            {/* Message List */}
            {messages?.map((message: MessageInterface, index: number) => (
              <React.Fragment key={message.id}>
                <Message
                  messageId={message.id}
                  role={message.role}
                  content={message.content}
                  messageIndex={index}
                  isTurnStart={turnStartMessageIds.has(message.id)}
                />
                {!generating && !in_future && <NewMessageButton messageIndex={index} />}
              </React.Fragment>
            ))}

            {/* Composer Message */}
            <Message
              role={inputRole}
              content=''
              messageIndex={stickyIndex}
              isComposer
            />

            {/* Error Message */}
            {error && (
              <ErrorMessage
                error={error}
                onClose={() => setError('')}
              />
            )}

            {/* Download and Clone Buttons */}
            <div className={`mt-4 m-auto ${getWidthClass()}`}>
              {!generating && (
                <div className='md:w-[calc(100%-50px)] flex gap-4 flex-wrap justify-center'>
                  <DownloadChat saveRef={saveRef} />
                  <CloneChat />
                </div>
              )}
            </div>

            {/* Bottom Spacing */}
            <div className='w-full h-36' />
          </div>
        </div>
      </div>

      <ScrollToBottomButton
        show={showScrollButton}
        onClick={() => scrollToBottom()}
      />

      <PromptTurnNavigation
        turns={turns}
        activeTurnIndex={activeTurnIndex}
        onPrevious={() => goToTurn(-1)}
        onNext={() => goToTurn(1)}
      />
    </div>
  );
};

export default ChatContent;
